import { Mesh, Vector3 } from '@babylonjs/core';
import { SpawnPoint } from './AssetObject';
import { Collectible, CollectibleConfig, CollectibleFactory, CollectibleType } from './CollectibleFactory';
import { GameConfig } from './GameConfig';
import { Level, PathInfo } from './Level';
import { CollectibleDistributionConfig } from './LevelConfig';
import { PathFactory, PatternTypeConfig } from './PathFactory';

/**
 * Spawn point for collectibles - stores position data but delays mesh creation
 */
interface CollectibleSpawnPoint extends SpawnPoint<CollectibleDistributionConfig> {
  id: string;
  position: Vector3;
  pathIndex: number;
  pathInfo: PathInfo;
  type: CollectibleType;
  spawned: boolean;
  collected: boolean;
}

/**
 * Generator for generating collectibles inside of a level with lazy spawn/despawn
 */
export class CollectibleGenerator {
  /**
   * Type-dependent probabilities for collectibles
   * Higher values = more likely to be selected
   */
  private static COLLECTIBLE_PROBABILITIES: Record<CollectibleType, number> = {
    [CollectibleType.DIAMOND]: 1.0,
    [CollectibleType.COIN]: 0.75,
    [CollectibleType.TIME]: 0.25,
    [CollectibleType.LIFE]: 0.1,
    [CollectibleType.AMMO]: 0.75,
    [CollectibleType.AIRJUMP]: 0.125,
    [CollectibleType.SHIELD]: 0.125,
  };

  private static PATTERN_COLLECTIBLES = [CollectibleType.DIAMOND, CollectibleType.COIN, CollectibleType.AMMO];

  // Rotation speed for collectibles (radians per frame, same as RotationBehavior)
  private static readonly ROTATION_SPEED = 0.02;

  private spawnPoints: CollectibleSpawnPoint[] = [];
  private activeEntities: Map<string, Collectible> = new Map();
  private collectibles: Collectible[] = [];
  private spawnRange: number;
  private despawnRange: number;

  constructor(public level: Level) {}

  /**
   * Select a random collectible type based on weighted probabilities
   */
  private getWeightedRandomType(): CollectibleType {
    const types = Object.keys(CollectibleGenerator.COLLECTIBLE_PROBABILITIES) as CollectibleType[];
    const totalWeight = types.reduce((sum, type) => sum + CollectibleGenerator.COLLECTIBLE_PROBABILITIES[type], 0);

    let random = this.level.rng.uFloat32() * totalWeight;

    for (const type of types) {
      random -= CollectibleGenerator.COLLECTIBLE_PROBABILITIES[type];
      if (random <= 0) {
        return type;
      }
    }

    // Fallback (should not happen)
    return CollectibleType.COIN;
  }

  getPatternTypes(maxPatternSize: number): PatternTypeConfig[] {
    const size = this.level.integerInRange(1, maxPatternSize);
    return [
      // {
      //   type: 'halfCircleDepth',
      //   segments: size,
      //   depth: size,
      // },
      // {
      //   type: 'depthLine',
      //   segments: size,
      //   depth: size,
      // },
      {
        type: 'horizontalLine',
        segments: size,
        width: size,
      },
      {
        type: 'verticalLine',
        segments: size,
        height: size,
      },
      {
        type: 'halfCircle',
        segments: size,
        width: size,
      },
    ];
  }

  async generate(distribution?: CollectibleDistributionConfig): Promise<void> {
    this.spawnRange = distribution.spawnRange;
    this.despawnRange = distribution.despawnRange;

    const minPoint = distribution?.minPoint || 3;
    const maxPoint = distribution?.maxPoint || this.level.pathPoints.length - 3;
    const interval = distribution?.interval ?? 1;

    let spawnPointId = 0;

    for (let i = 0; i < this.level.pathPoints.length; i += interval) {
      if (i < minPoint || i > maxPoint) {
        continue;
      }
      if (this.level.occupiedPathIndexes.includes(i)) {
        continue;
      }
      const probability = this.level.rng.uFloat32();
      if (distribution?.probability && probability > distribution.probability) {
        continue;
      }
      if (distribution?.skipProbability && probability < distribution.skipProbability) {
        continue;
      }

      let position = this.level.getMaxPathPosition(i);

      if (this.level.config.platform && !this.level.config.way) {
        const platform = this.level.getPlatformByPathIndex(i);
        if (!platform) {
          position.y += distribution.offset?.y || 0;
        }
      }

      if (distribution.offset) {
        position.addInPlace(distribution.offset);
      }

      const patternTypes = this.getPatternTypes(distribution.maxPatternSize);
      const patternType = this.level.randomArrayElement(patternTypes);
      const pathInfo = this.level.getPathInfo(i);
      let type = this.getWeightedRandomType();

      const positions: Vector3[] = CollectibleGenerator.PATTERN_COLLECTIBLES.includes(type)
        ? PathFactory.getPatternPositions({ preset: patternType }, position, this.level.config.path.laneWidth, pathInfo, true)
        : [position];

      for (const pos of positions) {
        this.spawnPoints.push({
          id: `collectible_${spawnPointId++}`,
          position: pos,
          pathIndex: i,
          pathInfo,
          type,
          distribution: distribution,
          assetObject: undefined,
          spawned: false,
          collected: false,
        });

        if (distribution.mixPatternTypes) {
          type = this.getWeightedRandomType();
        }
      }
    }
    this.level.collectibles = this.spawnPoints.length;
    if (GameConfig.logDebug) {
      console.log(`[CollectibleGenerator] Created ${this.spawnPoints.length} spawn points`);
    }
  }

  update(deltaTime: number): void {
    if (!this.level.player) return;

    // Check for spawning
    for (const spawnPoint of this.spawnPoints) {
      if (spawnPoint.spawned || spawnPoint.collected) continue;

      const distance = this.level.getCameraDistance(spawnPoint.position);
      if (distance <= this.spawnRange) {
        this.spawnCollectible(spawnPoint);
      }
    }

    // Check for despawning and update active collectibles
    for (const [id, collectible] of this.activeEntities) {
      // Check if collected
      if (collectible.collected) {
        const spawnPoint = this.spawnPoints.find((sp) => sp.id === id);
        if (spawnPoint) {
          spawnPoint.collected = true;
        }
        this.activeEntities.delete(id);
        continue;
      }

      // Rotate collectible around Y axis (replaces RotationBehavior for instances)
      if (collectible.mesh && !collectible.mesh.isDisposed()) {
        collectible.mesh.rotation.y += CollectibleGenerator.ROTATION_SPEED;
      }

      // Check for despawn (only if player moved past it)
      const distance = this.level.getCameraDistance(collectible.mesh.position);
      if (distance > this.despawnRange) {
        this.despawnCollectible(id);
      }
    }
  }

  private spawnCollectible(spawnPoint: CollectibleSpawnPoint): void {
    const config: CollectibleConfig = {
      position: spawnPoint.position,
      type: spawnPoint.type,
    };

    const collectible = CollectibleFactory.create(config, this.level, spawnPoint.pathIndex);

    // Register collision mesh with player so collectible can be picked up
    this.level.player?.registerCollisionMesh(collectible.mesh);

    this.activeEntities.set(spawnPoint.id, collectible);
    spawnPoint.spawned = true;
  }

  private despawnCollectible(id: string): void {
    const collectible = this.activeEntities.get(id);
    if (!collectible) return;

    // Dispose mesh
    if (collectible.mesh) {
      // Remove from collision meshes if registered
      this.level.player?.unregisterCollisionMesh(collectible.mesh as Mesh);
      collectible.mesh.dispose();
    }

    this.activeEntities.delete(id);

    // Mark spawn point as not spawned so it can respawn if player returns
    // (but only if it wasn't collected)
    const spawnPoint = this.spawnPoints.find((sp) => sp.id === id);
    if (spawnPoint && !spawnPoint.collected) {
      spawnPoint.spawned = false;
    }
  }

  dispose(): void {
    for (const [id, collectible] of this.activeEntities) {
      if (collectible.mesh) {
        collectible.mesh.dispose();
      }
    }

    CollectibleFactory.dispose();
    this.activeEntities.clear();
    this.spawnPoints = [];
  }
}
